import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowPathIcon,
    PlusIcon,
    XMarkIcon,
    CalendarDaysIcon,
    NewspaperIcon,
    BoltIcon,
    FireIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import Sparkline from '../components/Sparkline';
import timeAgo from '../utils/timeAgo';
import REFRESH_INTERVALS, { useAutoRefresh } from '../config/refreshConfig';
import { useLanguage } from '../contexts/LanguageContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { fmtNum, fmtMarketCap } from '../utils/formatters';
import { getChartTrendColor, getQuoteChangeColor } from '../utils/marketChart';
import ErrorMessage from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';

const PERIOD_OPTIONS = ['1D', '5D', '1M', '3M', '6M', '1Y'];
const PERIOD_API_MAP = { '1D': '1d', '5D': '5d', '1M': '1mo', '3M': '3mo', '6M': '6mo', '1Y': '1y' };
const ARK_FUNDS = ['ARKK', 'ARKW', 'ARKF', 'ARKG', 'ARKQ'];
const TRENDING_POOL = ['COIN', 'SHOP', 'SNOW', 'SOXX', 'MU', 'AMAT', 'ORCL', 'MRVL', 'SMCI', 'ON'];

// ---- Portfolio chart constants ----
const PORTFOLIO_MAX_STOCKS = 8;
const PORTFOLIO_PERIOD_OPTIONS = ['1M', '3M', '6M', '1Y'];
const PORTFOLIO_PERIOD_API_MAP = { '1M': '1mo', '3M': '3mo', '6M': '6mo', '1Y': '1y' };
const PORTFOLIO_LINE_COLORS = ['#007AFF', '#FF3B30', '#34C759', '#FF9500', '#5856D6', '#AF52DE', '#00C7BE', '#FF2D55'];

// ======== Mini Sparkline (compact for cards) ========
const MiniSparkline = ({ prices, color, width = 80, height = 40 }) => {
    if (!prices || prices.length < 2) return <div style={{ width, height }} />;
    const closes = prices.map(p => p.close);
    const min = Math.min(...closes);
    const max = Math.max(...closes);
    const range = max - min || 1;
    const padY = 4;
    const points = closes.map((v, i) => {
        const x = (i / (closes.length - 1)) * 100;
        const y = padY + ((max - v) / range) * (height - padY * 2);
        return `${x},${y}`;
    }).join(' ');
    const areaPoints = `0,${height} ${points} 100,${height}`;
    const gradId = `mini-${color.replace('#', '')}`;
    return (
        <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" style={{ width, height, display: 'block' }}>
            <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.2" />
                    <stop offset="100%" stopColor={color} stopOpacity="0.02" />
                </linearGradient>
            </defs>
            <polygon points={areaPoints} fill={`url(#${gradId})`} />
            <polyline points={points} fill="none" stroke={color} strokeWidth="1" strokeLinejoin="round" />
        </svg>
    );
};

// ======== Shared styles ========
const card = {
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: '24px',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.04)',
    padding: '1.25rem',
};

const tag = {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: '6px',
    fontSize: '0.7rem',
    fontWeight: '600',
    textTransform: 'uppercase',
};

const sectionIcon = {
    width: '18px',
    height: '18px',
    marginRight: '6px',
    color: '#86868B',
};

// ================================================================
// WorkbenchPage
// ================================================================
const WorkbenchPage = () => {
    const navigate = useNavigate();
    const { language, t } = useLanguage();
    const isZh = language === 'zh';
    // ---- Watchlist & portfolio from PreferencesContext ----
    const { watchlist, addToWatchlist, removeFromWatchlist,
            portfolioStocks, setPortfolioStocks,
            portfolios, savePortfolio, deletePortfolio, loadPortfolio } = usePreferences();
    const [newSymbol, setNewSymbol] = useState('');

    // ---- Market data (init from API cache to avoid spinner on nav) ----
    const [cachedMarket] = useState(() => apiService.peekCache(`snapshot:${[...watchlist, ...TRENDING_POOL.filter(s => !watchlist.includes(s))].join(',')}`));
    const cachedMarketList = useMemo(() => {
        const d = cachedMarket;
        const data = d?.data || d?.quotes || d || [];
        return Array.isArray(data) ? data : [];
    }, [cachedMarket]);
    const [quotes, setQuotes] = useState(cachedMarketList);
    const [sparklines, setSparklines] = useState({});
    const [loading, setLoading] = useState(cachedMarketList.length === 0);
    const [marketError, setMarketError] = useState(null);

    // ---- Detail panel ----
    const [selectedSymbol, setSelectedSymbol] = useState(null);
    const [companyProfile, setCompanyProfile] = useState(null);
    const [companyNews, setCompanyNews] = useState([]);
    const [bullBearNews, setBullBearNews] = useState({ bull: [], bear: [], summary: '', overall_sentiment: '', key_catalysts: [], risk_factors: [], generated_at: null });
    const [detailChart, setDetailChart] = useState(null);
    const [detailPeriod, setDetailPeriod] = useState('1M');
    const [detailLoading, setDetailLoading] = useState(false);
    const [profileLoading, setProfileLoading] = useState(false);

    // ---- Today's events ----
    const [earningsEvents, setEarningsEvents] = useState([]);
    const [aiEvents, setAiEvents] = useState([]);

    // ---- Watchlist Pulse ----
    const [pulseNews, setPulseNews] = useState([]);
    const [pulseLoading, setPulseLoading] = useState(false);

    // ---- ARK ----
    const [selectedFund, setSelectedFund] = useState('ARKK');
    const [arkHoldings, setArkHoldings] = useState([]);
    const [arkLoading, setArkLoading] = useState(false);
    const [arkChanges, setArkChanges] = useState([]);

    // ---- Guru ----
    const [investors, setInvestors] = useState([]);
    const [selectedInvestor, setSelectedInvestor] = useState(null);
    const [holdings, setHoldings] = useState([]);
    const [investorInfo, setInvestorInfo] = useState(null);

    const chartCache = useRef({});

    // ---- Portfolio chart state ----
    const [portfolioPeriod, setPortfolioPeriod] = useState('3M');
    const [portfolioData, setPortfolioData] = useState({});
    const [portfolioLoading, setPortfolioLoading] = useState(false);
    const [portfolioHover, setPortfolioHover] = useState(null);
    const [portfolioInput, setPortfolioInput] = useState('');
    const [portfolioCustomStart, setPortfolioCustomStart] = useState('');
    const [portfolioCustomEnd, setPortfolioCustomEnd] = useState('');

    // Trending = TRENDING_POOL minus watchlist
    const trendingSymbols = useMemo(() => {
        return TRENDING_POOL.filter(s => !watchlist.includes(s));
    }, [watchlist]);

    // All symbols = watchlist + trending (for batch quote fetch)
    const allSymbols = useMemo(() => {
        return [...watchlist, ...trendingSymbols];
    }, [watchlist, trendingSymbols]);

    const symbolsStr = useMemo(() => allSymbols.join(','), [allSymbols]);

    // ARK change stats
    const arkChangeStats = useMemo(() => {
        const stats = { new: 0, increase: 0, decrease: 0, sold_out: 0 };
        arkChanges.forEach(c => { if (stats[c.change_type] !== undefined) stats[c.change_type]++; });
        return stats;
    }, [arkChanges]);

    // Investment signals (computed from ARK + Guru data)
    const investmentSignals = useMemo(() => {
        const arkTickers = new Set(arkHoldings.map(h => h.ticker || h.symbol));
        const guruTickers = new Set(holdings.map(h => h.ticker || h.symbol));
        const consensus = [...arkTickers].filter(t => guruTickers.has(t)).map(ticker => {
            const arkH = arkHoldings.find(h => (h.ticker || h.symbol) === ticker);
            const guruH = holdings.find(h => (h.ticker || h.symbol) === ticker);
            return { ticker, arkWeight: arkH?.weight, guruWeight: guruH?.portfolio_percent, company: arkH?.company || guruH?.company || guruH?.name };
        });
        const bigBuys = arkChanges
            .filter(c => c.change_type === 'new' || c.change_type === 'increase')
            .sort((a, b) => Math.abs(b.shares_change_pct || 0) - Math.abs(a.shares_change_pct || 0))
            .slice(0, 8);
        const exits = [
            ...arkChanges.filter(c => c.change_type === 'sold_out').map(c => ({ ticker: c.ticker, company: c.company, source: 'ARK', type: 'sold_out' })),
            ...holdings.filter(h => h.shares_change != null && h.shares_change < 0).map(h => ({ ticker: h.ticker || h.symbol, company: h.company || h.name, source: 'Guru', type: 'decrease', changePct: h.shares_change_percent })),
        ];
        return { consensus, bigBuys, exits };
    }, [arkHoldings, holdings, arkChanges]);

    // Categorize events by watchlist relevance
    const categorizedEvents = useMemo(() => {
        const watchlistSet = new Set(watchlist.map(s => s.toUpperCase()));
        const relatedEarnings = earningsEvents.filter(ev => watchlistSet.has(ev.symbol?.toUpperCase()));
        const otherEarnings = earningsEvents.filter(ev => !watchlistSet.has(ev.symbol?.toUpperCase()));
        const matchesWatchlist = (title) => {
            if (!title) return false;
            const upper = title.toUpperCase();
            return [...watchlistSet].some(sym => {
                const regex = new RegExp(`\\b${sym}\\b`);
                return regex.test(upper);
            });
        };
        const relatedAI = aiEvents.filter(ev => matchesWatchlist(ev.title));
        const otherAI = aiEvents.filter(ev => !matchesWatchlist(ev.title));
        return { relatedEarnings, otherEarnings, relatedAI, otherAI };
    }, [earningsEvents, aiEvents, watchlist]);

    // Aggregate pulse news by stock + enrich with signals
    const pulseByStock = useMemo(() => {
        const grouped = {};
        pulseNews.forEach(n => {
            const sym = n._symbol;
            if (!grouped[sym]) grouped[sym] = { symbol: sym, news: [], signals: [] };
            grouped[sym].news.push(n);
        });
        Object.values(grouped).forEach(group => {
            const sym = group.symbol.toUpperCase();
            const arkChange = arkChanges.find(c => (c.ticker || '').toUpperCase() === sym);
            if (arkChange) {
                const label = { new: 'ARK New', increase: 'ARK Buy', decrease: 'ARK Trim', sold_out: 'ARK Exit' }[arkChange.change_type];
                const color = (arkChange.change_type === 'new' || arkChange.change_type === 'increase') ? '#34C759' : '#FF3B30';
                if (label) group.signals.push({ label, color, bg: color === '#34C759' ? 'rgba(52,199,89,0.1)' : 'rgba(255,59,48,0.1)' });
            }
            const guruHold = holdings.find(h => ((h.ticker || h.symbol || '')).toUpperCase() === sym);
            if (guruHold) {
                if (guruHold.shares_change > 0) group.signals.push({ label: 'Guru Buy', color: '#34C759', bg: 'rgba(52,199,89,0.1)' });
                else if (guruHold.shares_change < 0) group.signals.push({ label: 'Guru Trim', color: '#FF9500', bg: 'rgba(255,149,0,0.1)' });
                else group.signals.push({ label: 'Guru Hold', color: '#007AFF', bg: 'rgba(0,122,255,0.1)' });
            }
            if (investmentSignals.consensus.some(c => c.ticker.toUpperCase() === sym)) {
                group.signals.push({ label: 'Consensus', color: '#5856D6', bg: 'rgba(88,86,214,0.1)' });
            }
        });
        return Object.values(grouped);
    }, [pulseNews, arkChanges, holdings, investmentSignals]);

    // Portfolio chart: compute return percentages from raw price data
    const portfolioChartData = useMemo(() => {
        const syms = portfolioStocks.filter(s => portfolioData[s]?.length > 1);
        if (syms.length === 0) return null;

        // Build date->price maps
        const dateMaps = {};
        syms.forEach(s => {
            dateMaps[s] = {};
            portfolioData[s].forEach(p => {
                const d = (p.date || '').slice(0, 10);
                if (d) dateMaps[s][d] = p.close;
            });
        });

        // Find common dates, filter by custom range if active
        const allDates = Object.keys(dateMaps[syms[0]]).sort();
        let commonDates = allDates.filter(d => syms.every(s => dateMaps[s][d] != null));
        if (portfolioPeriod === 'CUSTOM' && portfolioCustomStart && portfolioCustomEnd) {
            commonDates = commonDates.filter(d => d >= portfolioCustomStart && d <= portfolioCustomEnd);
        }
        if (commonDates.length < 2) return null;

        // Calculate returns
        const series = {};
        syms.forEach(s => {
            const first = dateMaps[s][commonDates[0]];
            series[s] = commonDates.map(d => ((dateMaps[s][d] - first) / first) * 100);
        });

        // Portfolio = equal-weight average
        const portfolioReturns = commonDates.map((_, i) => {
            const sum = syms.reduce((acc, s) => acc + series[s][i], 0);
            return sum / syms.length;
        });

        // Y range
        const allVals = [...Object.values(series).flat(), ...portfolioReturns];
        const minY = Math.min(...allVals);
        const maxY = Math.max(...allVals);
        const padY = (maxY - minY) * 0.1 || 5;

        return { syms, commonDates, series, portfolioReturns, minY: minY - padY, maxY: maxY + padY };
    }, [portfolioStocks, portfolioData, portfolioPeriod, portfolioCustomStart, portfolioCustomEnd]);


    // ============================================================
    // Data fetching
    // ============================================================

    // Fetch market quotes for all symbols (watchlist + trending)
    const hasMarketDataRef = useRef(cachedMarketList.length > 0);
    const fetchMarketData = useCallback(async () => {
        if (!hasMarketDataRef.current) setLoading(true);
        setMarketError(null);
        try {
            const res = await apiService.getMarketSnapshot(symbolsStr);
            const data = res?.data || res?.quotes || res || [];
            const list = Array.isArray(data) ? data : [];
            setQuotes(list);
            if (list.length > 0) hasMarketDataRef.current = true;
        } catch (err) {
            console.error('Failed to load market data:', err);
            setMarketError(isZh ? '市场数据加载失败' : 'Failed to load market data');
        } finally {
            setLoading(false);
        }
    }, [symbolsStr, isZh]);

    // Fetch mini sparklines for all symbols (5d)
    const fetchSparklines = useCallback(async () => {
        const syms = allSymbols.slice(0, 16); // limit batch
        const results = {};
        const promises = syms.map(async (sym) => {
            try {
                const res = await apiService.getMarketChart(sym, '5d');
                const data = res?.data || res;
                if (data?.prices) results[sym] = data.prices;
            } catch (err) {
                // Silent fail for individual sparkline - don't break batch
                console.debug(`Failed to load sparkline for ${sym}:`, err.message);
            }
        });
        await Promise.allSettled(promises);
        setSparklines(prev => ({ ...prev, ...results }));
    }, [allSymbols]);

    // Fetch today's events
    const fetchEvents = useCallback(async () => {
        try {
            const [eventsRes, aiRes] = await Promise.allSettled([
                apiService.getUpcomingEvents(7),
                apiService.getAIEvents(5),
            ]);
            if (eventsRes.status === 'fulfilled') {
                const data = eventsRes.value?.data || eventsRes.value?.events || eventsRes.value || [];
                const events = Array.isArray(data) ? data : [];
                // Filter to today
                const today = new Date().toISOString().slice(0, 10);
                const todayEvents = events.filter(e => e.date && e.date.startsWith(today));
                setEarningsEvents(todayEvents.length > 0 ? todayEvents : events.slice(0, 8));
            }
            if (aiRes.status === 'fulfilled') {
                const raw = aiRes.value?.data || aiRes.value || {};
                const data = raw?.events || (Array.isArray(raw) ? raw : []);
                setAiEvents(Array.isArray(data) ? data.slice(0, 5) : []);
            }
        } catch (err) {
            console.error('Failed to load events:', err);
        }
    }, []);

    // Fetch watchlist pulse (news for watched companies)
    const fetchPulse = useCallback(async () => {
        setPulseLoading(true);
        try {
            const syms = watchlist.slice(0, 6);
            const allNews = [];
            const promises = syms.map(async (sym) => {
                try {
                    const res = await apiService.getCompanyNews(sym, 3);
                    const data = res?.data?.news || res?.data || res?.news || res || [];
                    const items = Array.isArray(data) ? data : [];
                    items.forEach(n => allNews.push({ ...n, _symbol: sym }));
                } catch (err) {
                    // Silent fail for individual company news - don't break batch
                    console.debug(`Failed to load news for ${sym}:`, err.message);
                }
            });
            await Promise.allSettled(promises);
            allNews.sort((a, b) => new Date(b.published_at || b.date || 0) - new Date(a.published_at || a.date || 0));
            setPulseNews(allNews.slice(0, 12));
        } catch (err) {
            console.error('Failed to load pulse:', err);
        } finally {
            setPulseLoading(false);
        }
    }, [watchlist]);

    // Fetch portfolio chart data
    const fetchPortfolioCharts = useCallback(async () => {
        if (portfolioStocks.length === 0) { setPortfolioData({}); return; }
        let period;
        if (portfolioPeriod === 'CUSTOM') {
            if (!portfolioCustomStart || !portfolioCustomEnd) return;
            const daysAgo = Math.ceil((Date.now() - new Date(portfolioCustomStart).getTime()) / 86400000);
            period = daysAgo <= 30 ? '1mo' : daysAgo <= 90 ? '3mo' : daysAgo <= 180 ? '6mo' : '1y';
        } else {
            period = PORTFOLIO_PERIOD_API_MAP[portfolioPeriod] || '3mo';
        }
        setPortfolioLoading(true);
        try {
            const results = {};
            const promises = portfolioStocks.map(async (sym) => {
                try {
                    const res = await apiService.getMarketChart(sym, period);
                    const data = res?.prices ? res : (res?.data || null);
                    if (data?.prices) results[sym] = data.prices;
                } catch (err) {
                    console.debug(`Failed to load portfolio chart for ${sym}:`, err.message);
                }
            });
            await Promise.allSettled(promises);
            setPortfolioData(results);
        } catch (err) {
            console.error('Failed to load portfolio charts:', err);
        } finally {
            setPortfolioLoading(false);
        }
    }, [portfolioStocks, portfolioPeriod, portfolioCustomStart, portfolioCustomEnd]);

    // Fetch ARK data
    const fetchArkData = useCallback(async () => {
        setArkLoading(true);
        try {
            const [holdingsRes, changesRes] = await Promise.allSettled([
                apiService.getArkHoldings(selectedFund, 10),
                apiService.getArkChanges(selectedFund, 7),
            ]);
            if (holdingsRes.status === 'fulfilled') {
                const res = holdingsRes.value;
                const data = res?.data?.holdings || res?.data || res?.holdings || res || [];
                setArkHoldings(Array.isArray(data) ? data : []);
            }
            if (changesRes.status === 'fulfilled') {
                const data = changesRes.value?.data?.changes || changesRes.value?.changes || [];
                setArkChanges(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            console.error('Failed to load ARK data:', err);
        } finally {
            setArkLoading(false);
        }
    }, [selectedFund]);

    // Fetch investors
    const fetchInvestors = useCallback(async () => {
        try {
            const res = await apiService.getInvestorsList();
            const data = res?.data || res?.investors || res || [];
            const list = Array.isArray(data) ? data : [];
            setInvestors(list);
            if (list.length > 0 && !selectedInvestor) {
                setSelectedInvestor(list[0].key || list[0].name);
            }
        } catch (err) {
            console.error('Failed to load investors:', err);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch company detail
    const fetchCompanyDetail = useCallback(async (symbol) => {
        if (!symbol) return;
        setProfileLoading(true);
        setCompanyProfile(null);
        setCompanyNews([]);
        setBullBearNews({ bull: [], bear: [], summary: '', overall_sentiment: '', key_catalysts: [], risk_factors: [], generated_at: null });
        try {
            const [profileRes, newsRes, analysisRes] = await Promise.allSettled([
                apiService.getCompanyProfile(symbol),
                apiService.getCompanyNews(symbol, 8),
                apiService.getCompanyAnalysis(symbol, 7, language === 'zh' ? 'zh' : 'en'),
            ]);
            if (profileRes.status === 'fulfilled') {
                const raw = profileRes.value?.data || profileRes.value || null;
                setCompanyProfile(raw?.company || raw || null);
            }
            if (newsRes.status === 'fulfilled') {
                const raw = newsRes.value?.data || newsRes.value || {};
                const data = raw?.news || (Array.isArray(raw) ? raw : []);
                setCompanyNews(Array.isArray(data) ? data : []);
            }
            // Parse bull/bear from AI analysis API
            if (analysisRes.status === 'fulfilled') {
                const analyses = analysisRes.value?.data?.analyses || [];
                if (analyses.length > 0) {
                    const latest = analyses[0];
                    const isZh = language === 'zh';
                    setBullBearNews({
                        bull: (latest.bull_points || []).slice(0, 5).map(p => ({
                            ...p,
                            title: (isZh && p.title_zh) || p.title,
                            detail: (isZh && p.detail_zh) || p.detail,
                        })),
                        bear: (latest.bear_points || []).slice(0, 5).map(p => ({
                            ...p,
                            title: (isZh && p.title_zh) || p.title,
                            detail: (isZh && p.detail_zh) || p.detail,
                        })),
                        summary: (isZh && latest.summary_zh) || latest.summary || '',
                        overall_sentiment: latest.overall_sentiment || '',
                        key_catalysts: (isZh && latest.key_catalysts_zh) || latest.key_catalysts || [],
                        risk_factors: (isZh && latest.risk_factors_zh) || latest.risk_factors || [],
                        generated_at: latest.generated_at,
                    });
                }
            }
        } catch (err) {
            console.error('Failed to load company detail:', err);
        } finally {
            setProfileLoading(false);
        }
    }, [language]);

    // Fetch detail chart
    const fetchDetailChart = useCallback(async () => {
        if (!selectedSymbol) return;
        const cacheKey = `${selectedSymbol}:${detailPeriod}`;
        const cached = chartCache.current[cacheKey];
        if (cached && Date.now() - cached.ts < 60000) {
            setDetailChart(cached.data);
            return;
        }
        setDetailLoading(true);
        setDetailChart(null);
        try {
            const period = PERIOD_API_MAP[detailPeriod] || '1mo';
            const res = await apiService.getMarketChart(selectedSymbol, period);
            const data = res?.prices ? res : (res?.data || null);
            setDetailChart(data);
            chartCache.current[cacheKey] = { data, ts: Date.now() };
        } catch (err) {
            console.error('Failed to load detail chart:', err);
            setDetailChart(null);
        } finally {
            setDetailLoading(false);
        }
    }, [selectedSymbol, detailPeriod]);

    // ============================================================
    // Effects
    // ============================================================

    // Initial load
    useEffect(() => { fetchMarketData(); }, [fetchMarketData]);
    useEffect(() => { fetchSparklines(); }, [fetchSparklines]);
    useEffect(() => { fetchEvents(); }, [fetchEvents]);
    useEffect(() => { fetchPulse(); }, [fetchPulse]);
    useEffect(() => { fetchArkData(); }, [fetchArkData]);
    useEffect(() => { fetchInvestors(); }, [fetchInvestors]);

    // Auto-refresh
    useAutoRefresh(() => { fetchMarketData(); fetchSparklines(); }, REFRESH_INTERVALS.workbench.market);
    useAutoRefresh(() => { fetchEvents(); fetchPulse(); }, REFRESH_INTERVALS.workbench.events);
    useAutoRefresh(() => { fetchArkData(); fetchInvestors(); }, REFRESH_INTERVALS.workbench.discovery);

    // Detail panel
    useEffect(() => {
        if (selectedSymbol) fetchCompanyDetail(selectedSymbol);
    }, [selectedSymbol, fetchCompanyDetail]);

    useEffect(() => { fetchDetailChart(); }, [fetchDetailChart]);

    // Investor holdings + info
    useEffect(() => {
        if (!selectedInvestor) return;
        Promise.allSettled([
            apiService.getInvestorHoldings(selectedInvestor, 10),
            apiService.getInvestorInfo(selectedInvestor),
        ]).then(([holdingsRes, infoRes]) => {
            if (holdingsRes.status === 'fulfilled') {
                const res = holdingsRes.value;
                const data = res?.data?.holdings || res?.data || res?.holdings || res || [];
                setHoldings(Array.isArray(data) ? data : []);
            }
            if (infoRes.status === 'fulfilled') {
                const res = infoRes.value;
                setInvestorInfo(res?.data || res || null);
            }
        });
    }, [selectedInvestor]);

    // Portfolio chart
    useEffect(() => { fetchPortfolioCharts(); }, [fetchPortfolioCharts]);

    // ============================================================
    // Actions
    // ============================================================

    const [addingSymbol, setAddingSymbol] = useState(false);
    const [addError, setAddError] = useState('');
    const [hoveredCard, setHoveredCard] = useState(null);

    const addSymbol = async () => {
        const s = newSymbol.trim().toUpperCase();
        if (!s) return;
        setAddError('');

        // Already in watchlist
        if (watchlist.includes(s)) {
            setAddError(isZh ? `「${s}」已在自选股中` : `"${s}" is already in watchlist`);
            return;
        }

        // Check local quotes cache first
        const cached = quotes.find(q => q.symbol === s);
        if (cached) {
            if (cached.price > 0) {
                addToWatchlist(s);
                setNewSymbol('');
            } else {
                setAddError(isZh ? `未找到「${s}」，请检查股票代码` : `"${s}" not found, please check the symbol`);
            }
            return;
        }

        // Query API to validate
        setAddingSymbol(true);
        try {
            const res = await apiService.getMarketSnapshot(s);
            const data = res?.data || res?.quotes || res || [];
            const list = Array.isArray(data) ? data : [];
            const quote = list.find(q => q.symbol === s);
            if (quote && quote.price > 0) {
                addToWatchlist(s);
                setNewSymbol('');
                fetchMarketData();
            } else {
                setAddError(isZh ? `未找到「${s}」，请检查股票代码` : `"${s}" not found, please check the symbol`);
            }
        } catch {
            setAddError(isZh ? '验证失败，请稍后重试' : 'Validation failed, please retry');
        } finally {
            setAddingSymbol(false);
        }
    };

    const removeSymbol = (sym) => {
        removeFromWatchlist(sym);
        if (selectedSymbol === sym) setSelectedSymbol(null);
    };

    const handleCardClick = (sym) => {
        setSelectedSymbol(prev => prev === sym ? null : sym);
    };

    // Quote lookup
    const getQuote = (sym) => quotes.find(q => q.symbol === sym);

    // Portfolio stock toggle
    const togglePortfolioStock = (sym) => {
        if (portfolioStocks.includes(sym)) {
            setPortfolioStocks(portfolioStocks.filter(s => s !== sym));
        } else if (portfolioStocks.length < PORTFOLIO_MAX_STOCKS) {
            setPortfolioStocks([...portfolioStocks, sym]);
        }
    };
    const addPortfolioInput = () => {
        const s = portfolioInput.trim().toUpperCase();
        if (!s || portfolioStocks.includes(s) || portfolioStocks.length >= PORTFOLIO_MAX_STOCKS) return;
        setPortfolioStocks([...portfolioStocks, s]);
        setPortfolioInput('');
    };

    // ============================================================
    // Render
    // ============================================================

    const selectedQuote = getQuote(selectedSymbol);
    const detailQuoteColor = getQuoteChangeColor(selectedQuote?.change_percent);
    const detailChartColor = getChartTrendColor(detailChart?.prices, selectedQuote?.change_percent);

    return (
        <div>
            {/* ====== Row 1: Cards (left) + Watchlist Pulse (right) ====== */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                {/* Left: Watchlist + Trending cards */}
                <div style={{ ...card, padding: '1rem 1.25rem', minWidth: 0 }}>
                    {/* Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingBottom: '10px',
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        gap: '12px',
                    }}>
                        <span style={{ fontSize: '1.05rem', fontWeight: '700', color: '#1D1D1F', flexShrink: 0 }}>
                            {t('workbench.myWatchlist')}
                        </span>
                        <div style={{ display: 'flex', gap: '6px', flex: 1, maxWidth: '300px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', gap: '6px', flex: 1, minWidth: 0 }}>
                                <input
                                    value={newSymbol}
                                    onChange={(e) => { setNewSymbol(e.target.value); setAddError(''); }}
                                    onKeyDown={(e) => e.key === 'Enter' && addSymbol()}
                                    placeholder={t('workbench.addSymbol')}
                                    disabled={addingSymbol}
                                    style={{
                                        flex: 1,
                                        padding: '5px 10px',
                                        borderRadius: '8px',
                                        border: addError ? '1px solid #FF3B30' : '1px solid rgba(0,0,0,0.08)',
                                        fontSize: '0.8rem',
                                        outline: 'none',
                                        minWidth: 0,
                                        opacity: addingSymbol ? 0.6 : 1,
                                    }}
                                />
                                <button
                                    onClick={addSymbol}
                                    disabled={addingSymbol || !newSymbol.trim()}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '3px',
                                        padding: '5px 10px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        backgroundColor: addingSymbol || !newSymbol.trim() ? '#86868B' : '#007AFF',
                                        color: '#fff',
                                        fontSize: '0.78rem',
                                        fontWeight: '600',
                                        cursor: addingSymbol || !newSymbol.trim() ? 'not-allowed' : 'pointer',
                                        flexShrink: 0,
                                    }}
                                    aria-label={t('workbench.add') + ' symbol to watchlist'}
                                >
                                    {addingSymbol
                                        ? <ArrowPathIcon style={{ width: '12px', animation: 'spin 1s linear infinite' }} aria-hidden="true" />
                                        : <PlusIcon style={{ width: '12px' }} aria-hidden="true" />
                                    } {t('workbench.add')}
                                </button>
                            </div>
                            {addError && (
                                <span style={{ fontSize: '0.72rem', color: '#FF3B30', width: '100%' }}>{addError}</span>
                            )}
                        </div>
                        <button
                            onClick={() => { fetchMarketData(); fetchSparklines(); }}
                            disabled={loading}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', flexShrink: 0 }}
                            aria-label="Refresh market data"
                        >
                            <ArrowPathIcon style={{ width: '16px', color: '#86868B', animation: loading ? 'spin 1s linear infinite' : 'none' }} aria-hidden="true" />
                        </button>
                    </div>

                    {/* Watchlist cards scroll */}
                    <div style={{
                        display: 'flex',
                        gap: '10px',
                        overflowX: 'auto',
                        paddingTop: '12px',
                        paddingBottom: '4px',
                        scrollbarWidth: 'thin',
                    }}>
                        {loading && quotes.length === 0 ? (
                            <LoadingSpinner size="small" />
                        ) : marketError ? (
                            <div style={{ width: '100%', padding: '0.5rem' }}>
                                <ErrorMessage message={marketError} onRetry={() => { fetchMarketData(); fetchSparklines(); }} size="small" />
                            </div>
                        ) : watchlist.map(sym => {
                            const q = getQuote(sym);
                            const isUp = q?.change_percent >= 0;
                            const color = isUp ? '#34C759' : '#FF3B30';
                            const isSelected = selectedSymbol === sym;
                            const sparkPrices = sparklines[sym];
                            return (
                                <div
                                    key={sym}
                                    onClick={() => handleCardClick(sym)}
                                    onMouseEnter={() => setHoveredCard(sym)}
                                    onMouseLeave={() => setHoveredCard(null)}
                                    style={{
                                        flex: '0 0 auto',
                                        width: '130px',
                                        padding: '10px 12px 8px',
                                        borderRadius: '16px',
                                        border: isSelected ? `2px solid ${color}` : '1px solid rgba(0,0,0,0.06)',
                                        backgroundColor: isSelected ? 'rgba(0,0,0,0.02)' : '#fff',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                        position: 'relative',
                                    }}
                                >
                                    {hoveredCard === sym && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); removeSymbol(sym); }}
                                            style={{
                                                position: 'absolute',
                                                top: '4px',
                                                right: '4px',
                                                background: 'rgba(0,0,0,0.06)',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: '3px',
                                                color: '#86868B',
                                                lineHeight: 0,
                                                borderRadius: '50%',
                                            }}
                                            aria-label={`Remove ${sym} from watchlist`}
                                        >
                                            <XMarkIcon style={{ width: '12px' }} aria-hidden="true" />
                                        </button>
                                    )}
                                    <div style={{ fontSize: '0.88rem', fontWeight: '700', color: '#1D1D1F' }}>{sym}</div>
                                    <div style={{ fontSize: '0.65rem', color: '#86868B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '3px' }}>
                                        {typeof q?.name === 'string' ? q.name : sym}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px', marginBottom: '3px' }}>
                                        <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#1D1D1F' }}>
                                            {q?.price != null ? fmtNum(q.price) : '--'}
                                        </span>
                                        <span style={{ fontSize: '0.68rem', fontWeight: '600', color }}>
                                            {q?.change_percent != null ? `${isUp ? '+' : ''}${q.change_percent.toFixed(2)}%` : ''}
                                        </span>
                                    </div>
                                    <MiniSparkline prices={sparkPrices} color={color} width={106} height={32} />
                                </div>
                            );
                        })}
                    </div>

                    {/* Trending cards scroll */}
                    {trendingSymbols.length > 0 && (
                        <>
                            <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#86868B', marginTop: '10px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <FireIcon style={{ width: '14px', color: '#FF9500' }} />
                                {t('workbench.trending')}
                            </div>
                            <div style={{
                                display: 'flex',
                                gap: '10px',
                                overflowX: 'auto',
                                paddingBottom: '4px',
                                scrollbarWidth: 'thin',
                            }}>
                                {trendingSymbols.map(sym => {
                                    const q = getQuote(sym);
                                    const isUp = q?.change_percent >= 0;
                                    const color = isUp ? '#34C759' : '#FF3B30';
                                    const isSelected = selectedSymbol === sym;
                                    const sparkPrices = sparklines[sym];
                                    return (
                                        <div
                                            key={sym}
                                            onClick={() => handleCardClick(sym)}
                                            style={{
                                                flex: '0 0 auto',
                                                width: '130px',
                                                padding: '10px 12px 8px',
                                                borderRadius: '16px',
                                                border: isSelected ? `2px solid ${color}` : '1px solid rgba(0,0,0,0.06)',
                                                backgroundColor: isSelected ? 'rgba(0,0,0,0.02)' : '#fff',
                                                cursor: 'pointer',
                                                transition: 'all 0.15s ease',
                                            }}
                                        >
                                            <div style={{ fontSize: '0.88rem', fontWeight: '700', color: '#1D1D1F' }}>{sym}</div>
                                            <div style={{ fontSize: '0.65rem', color: '#86868B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '3px' }}>
                                                {typeof q?.name === 'string' ? q.name : sym}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px', marginBottom: '3px' }}>
                                                <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#1D1D1F' }}>
                                                    {q?.price != null ? fmtNum(q.price) : '--'}
                                                </span>
                                                <span style={{ fontSize: '0.68rem', fontWeight: '600', color }}>
                                                    {q?.change_percent != null ? `${isUp ? '+' : ''}${q.change_percent.toFixed(2)}%` : ''}
                                                </span>
                                            </div>
                                            <MiniSparkline prices={sparkPrices} color={color} width={106} height={32} />
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                {/* Right: Today's Events + Watchlist Pulse */}
                <div style={{ display: 'flex', flexDirection: 'row', gap: '12px' }}>
                    {/* Today's Key Events */}
                    <div style={{ ...card, display: 'flex', flexDirection: 'column', padding: '0.8rem 1rem', maxHeight: '320px', flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1D1D1F', marginBottom: '8px' }}>
                            <CalendarDaysIcon style={{ width: '15px', display: 'inline', verticalAlign: 'middle', marginRight: '4px', color: '#86868B' }} />
                            {t('workbench.todaysKeyEvents')}
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', scrollbarWidth: 'thin' }}>
                            {/* Inline renderers */}
                            {(() => {
                                const renderEarningsRow = (ev, idx, highlighted) => (
                                    <div key={`e-${idx}`} style={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        padding: '4px 6px', marginBottom: '2px', borderRadius: '6px',
                                        ...(highlighted ? { borderLeft: '3px solid #007AFF', backgroundColor: 'rgba(0,122,255,0.04)' } : {}),
                                    }}>
                                        <div>
                                            <span style={{ fontSize: '0.78rem', fontWeight: '700', color: '#007AFF' }}>{ev.symbol}</span>
                                            <span style={{ fontSize: '0.65rem', color: '#86868B', marginLeft: '4px' }}>{ev.date}</span>
                                        </div>
                                        <div style={{ textAlign: 'right', fontSize: '0.68rem' }}>
                                            {ev.eps_estimate != null && <span style={{ color: '#666' }}>Est. ${ev.eps_estimate}</span>}
                                            {ev.hour && (
                                                <span style={{ ...tag, marginLeft: '4px', backgroundColor: ev.hour === 'bmo' ? 'rgba(255,149,0,0.1)' : 'rgba(88,86,214,0.1)', color: ev.hour === 'bmo' ? '#FF9500' : '#5856D6' }}>
                                                    {ev.hour === 'bmo' ? 'Pre-mkt' : ev.hour === 'amc' ? 'After-close' : ev.hour}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                                const renderAIRow = (ev, idx, highlighted) => (
                                    <div key={`ai-${idx}`} onClick={() => navigate(`/ai-event/${ev.id}`)} style={{
                                        display: 'block', padding: '4px 6px', marginBottom: '2px', borderRadius: '6px', cursor: 'pointer', color: 'inherit',
                                        ...(highlighted ? { borderLeft: '3px solid #007AFF', backgroundColor: 'rgba(0,122,255,0.04)' } : {}),
                                    }}>
                                        <div style={{ fontSize: '0.72rem', fontWeight: '500', color: '#1D1D1F', lineHeight: '1.3', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                            {(language === 'zh' && ev.title_zh) ? ev.title_zh : ev.title}
                                        </div>
                                        <div style={{ fontSize: '0.62rem', color: '#86868B', marginTop: '1px' }}>
                                            {ev.source && <span>{ev.source}</span>}
                                            {ev.source && (ev.published_at || ev.date) && <span> &middot; </span>}
                                            {(ev.published_at || ev.date) && <span>{timeAgo(ev.published_at || ev.date)}</span>}
                                        </div>
                                    </div>
                                );
                                const hasRelated = categorizedEvents.relatedEarnings.length > 0 || categorizedEvents.relatedAI.length > 0;
                                const hasOther = categorizedEvents.otherEarnings.length > 0 || categorizedEvents.otherAI.length > 0;
                                const hasAnyEvents = earningsEvents.length > 0 || aiEvents.length > 0;
                                return hasAnyEvents ? (
                                    <>
                                        {hasRelated && (
                                            <div style={{ marginBottom: '8px' }}>
                                                <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#007AFF', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <span>{t('workbench.relatedToYou')}</span>
                                                    <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(0,122,255,0.15)' }} />
                                                </div>
                                                {categorizedEvents.relatedEarnings.map((ev, idx) => renderEarningsRow(ev, idx, true))}
                                                {categorizedEvents.relatedAI.map((ev, idx) => renderAIRow(ev, idx, true))}
                                            </div>
                                        )}
                                        {hasOther && (
                                            <div>
                                                {hasRelated && (
                                                    <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#86868B', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <span style={{ whiteSpace: 'nowrap' }}>{t('workbench.otherEvents')}</span>
                                                        <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(0,0,0,0.06)' }} />
                                                    </div>
                                                )}
                                                {!hasRelated && (
                                                    <div style={{ fontSize: '0.68rem', fontWeight: '600', color: '#86868B', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                                                        {t('workbench.earningsAiEvents')}
                                                    </div>
                                                )}
                                                {categorizedEvents.otherAI.map((ev, idx) => renderAIRow(ev, idx, false))}
                                                {categorizedEvents.otherEarnings.map((ev, idx) => renderEarningsRow(ev, idx, false))}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div style={{ color: '#86868B', fontSize: '0.72rem', padding: '0.3rem 0' }}>
                                        {t('workbench.noEventsToday')}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>

                    {/* Watchlist Pulse */}
                    <div style={{ ...card, display: 'flex', flexDirection: 'column', padding: '0.8rem 1rem', maxHeight: '320px', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1D1D1F' }}>
                                <NewspaperIcon style={{ width: '15px', display: 'inline', verticalAlign: 'middle', marginRight: '4px', color: '#86868B' }} />
                                {t('workbench.watchlistPulse')}
                            </span>
                            {pulseLoading && <span style={{ fontSize: '0.7rem', color: '#86868B' }}>{t('workbench.updating')}</span>}
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', scrollbarWidth: 'thin', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {pulseByStock.length === 0 && !pulseLoading ? (
                                <div style={{ color: '#86868B', fontSize: '0.75rem', textAlign: 'center', padding: '1.5rem 0' }}>
                                    {t('workbench.noPulseNews')}
                                </div>
                            ) : (
                                pulseByStock.map(group => (
                                    <div key={group.symbol} style={{ border: '1px solid rgba(0,0,0,0.06)', borderRadius: '10px', padding: '8px', backgroundColor: '#fff' }}>
                                        {/* Stock header: ticker + signal pills */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '5px', flexWrap: 'wrap' }}>
                                            <span style={{ ...tag, backgroundColor: 'rgba(0,122,255,0.1)', color: '#007AFF', fontWeight: '700', fontSize: '0.68rem', padding: '2px 7px' }}>
                                                {group.symbol}
                                            </span>
                                            {group.signals.map((sig, si) => (
                                                <span key={si} style={{ ...tag, backgroundColor: sig.bg, color: sig.color, fontSize: '0.58rem', padding: '1px 6px' }}>
                                                    {sig.label}
                                                </span>
                                            ))}
                                        </div>
                                        {/* News list (max 3) */}
                                        {group.news.slice(0, 3).map((n, ni) => (
                                            <div key={ni} onClick={() => n.feed_id ? navigate(`/feed/${n.feed_id}`) : n.url && window.open(n.url, '_blank')} style={{
                                                padding: '3px 0', cursor: 'pointer',
                                                borderTop: ni > 0 ? '1px solid rgba(0,0,0,0.04)' : 'none',
                                            }}>
                                                {n.summary && (
                                                    <div style={{ fontSize: '0.65rem', color: '#444', lineHeight: '1.4', marginBottom: '2px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                        {n.summary.replace(/^[•\s]+/, '').split('\n')[0].replace(/^[•\s]+/, '')}
                                                    </div>
                                                )}
                                                <div style={{ fontSize: '0.6rem', color: '#86868B', lineHeight: '1.3', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                    {(language === 'zh' && n.title_zh) ? n.title_zh : n.title}
                                                </div>
                                                <div style={{ fontSize: '0.55rem', color: '#AEAEB2', marginTop: '1px' }}>
                                                    {n.source}{(n.published_at || n.date) ? ` \u00b7 ${timeAgo(n.published_at || n.date)}` : ''}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ====== Company Detail Panel ====== */}
            {selectedSymbol && (
                <div style={{
                    ...card,
                    marginBottom: '1.5rem',
                    padding: '1.25rem',
                    animation: 'fadeIn 0.2s ease',
                }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                            <span style={{ fontSize: '1.2rem', fontWeight: '700', color: '#1D1D1F' }}>
                                {typeof companyProfile?.name === 'string' ? companyProfile.name : (typeof selectedQuote?.name === 'string' ? selectedQuote.name : selectedSymbol)}
                            </span>
                            <span style={{ fontSize: '0.85rem', color: '#86868B' }}>{selectedSymbol}</span>
                            {selectedQuote?.price != null && (
                                <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1D1D1F' }}>
                                    ${fmtNum(selectedQuote.price)}
                                </span>
                            )}
                            {selectedQuote?.change_percent != null && (
                                <span style={{ fontSize: '0.88rem', fontWeight: '700', color: detailQuoteColor }}>
                                    {selectedQuote.change_percent >= 0 ? '+' : ''}{selectedQuote.change_percent.toFixed(2)}%
                                </span>
                            )}
                        </div>
                        <button
                            onClick={() => setSelectedSymbol(null)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#86868B' }}
                        >
                            <XMarkIcon style={{ width: '20px' }} />
                        </button>
                    </div>

                    {/* 4-column layout */}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr 2fr 2fr', gap: '1rem', minHeight: '240px' }}>
                        {/* Left: Company Info */}
                        <div style={{ fontSize: '0.82rem' }}>
                            {profileLoading ? (
                                <LoadingSpinner text={t('workbench.loadingProfile')} size="small" />
                            ) : companyProfile ? (
                                <div>
                                    {companyProfile.sector && (
                                        <div style={{ marginBottom: '6px' }}>
                                            <span style={{ ...tag, backgroundColor: 'rgba(0,122,255,0.08)', color: '#007AFF' }}>
                                                {companyProfile.sector}
                                            </span>
                                            {companyProfile.industry && (
                                                <span style={{ ...tag, backgroundColor: 'rgba(0,0,0,0.04)', color: '#666', marginLeft: '4px' }}>
                                                    {companyProfile.industry}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginTop: '12px' }}>
                                        <div>
                                            <div style={{ color: '#86868B', fontSize: '0.72rem' }}>{t('workbench.marketCap')}</div>
                                            <div style={{ fontWeight: '600' }}>{fmtMarketCap(companyProfile.market_cap)}</div>
                                        </div>
                                        <div>
                                            <div style={{ color: '#86868B', fontSize: '0.72rem' }}>{t('workbench.peRatio')}</div>
                                            <div style={{ fontWeight: '600' }}>{companyProfile.pe_ratio != null ? fmtNum(companyProfile.pe_ratio) : '--'}</div>
                                        </div>
                                        <div>
                                            <div style={{ color: '#86868B', fontSize: '0.72rem' }}>{t('workbench.epsLabel')}</div>
                                            <div style={{ fontWeight: '600' }}>{companyProfile.eps != null ? `$${fmtNum(companyProfile.eps)}` : '--'}</div>
                                        </div>
                                        <div>
                                            <div style={{ color: '#86868B', fontSize: '0.72rem' }}>{t('workbench.dividend')}</div>
                                            <div style={{ fontWeight: '600' }}>{companyProfile.dividend_yield != null ? `${fmtNum(companyProfile.dividend_yield)}%` : '--'}</div>
                                        </div>
                                        <div>
                                            <div style={{ color: '#86868B', fontSize: '0.72rem' }}>{t('workbench.high52w')}</div>
                                            <div style={{ fontWeight: '600' }}>{companyProfile['52_week_high'] != null ? `$${fmtNum(companyProfile['52_week_high'])}` : '--'}</div>
                                        </div>
                                        <div>
                                            <div style={{ color: '#86868B', fontSize: '0.72rem' }}>{t('workbench.low52w')}</div>
                                            <div style={{ fontWeight: '600' }}>{companyProfile['52_week_low'] != null ? `$${fmtNum(companyProfile['52_week_low'])}` : '--'}</div>
                                        </div>
                                        {companyProfile.employees && (
                                            <div>
                                                <div style={{ color: '#86868B', fontSize: '0.72rem' }}>{t('workbench.employees')}</div>
                                                <div style={{ fontWeight: '600' }}>{Number(companyProfile.employees).toLocaleString()}</div>
                                            </div>
                                        )}
                                        {companyProfile.beta != null && (
                                            <div>
                                                <div style={{ color: '#86868B', fontSize: '0.72rem' }}>{t('workbench.beta')}</div>
                                                <div style={{ fontWeight: '600' }}>{fmtNum(companyProfile.beta)}</div>
                                            </div>
                                        )}
                                    </div>
                                    {companyProfile.description && typeof companyProfile.description === 'string' && (
                                        <div style={{
                                            marginTop: '12px',
                                            fontSize: '0.75rem',
                                            color: '#666',
                                            lineHeight: '1.4',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                        }}>
                                            {companyProfile.description}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div style={{ color: '#86868B' }}>{t('workbench.noProfile')}</div>
                            )}
                        </div>

                        {/* Middle: Chart */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', gap: '2px', marginBottom: '8px' }}>
                                {PERIOD_OPTIONS.map(p => (
                                    <button
                                        key={p}
                                        onClick={() => setDetailPeriod(p)}
                                        style={{
                                            padding: '3px 10px',
                                            borderRadius: '6px',
                                            border: 'none',
                                            fontSize: '0.72rem',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            backgroundColor: detailPeriod === p ? '#1D1D1F' : 'transparent',
                                            color: detailPeriod === p ? '#fff' : '#86868B',
                                            transition: 'all 0.15s ease',
                                        }}
                                    >
                                        {p}
                                    </button>
                                ))}
                            </div>
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                {detailLoading ? (
                                    <div style={{ width: '100%', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <LoadingSpinner text={t('workbench.loadingChart')} size="small" />
                                    </div>
                                ) : (
                                    <Sparkline prices={detailChart?.prices} color={detailChartColor} height={200} />
                                )}
                            </div>
                        </div>

                        {/* Bull/Bear AI Analysis */}
                        <div style={{ overflowY: 'auto', maxHeight: '380px' }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: '600', color: '#1D1D1F', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>
                                    <BoltIcon style={{ ...sectionIcon, display: 'inline', verticalAlign: 'middle' }} />
                                    {t('workbench.bullBear')}
                                </span>
                                {bullBearNews.overall_sentiment && typeof bullBearNews.overall_sentiment === 'string' && (
                                    <span style={{
                                        fontSize: '0.62rem',
                                        fontWeight: '600',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        backgroundColor: bullBearNews.overall_sentiment.includes('bull') ? 'rgba(52,199,89,0.12)' : bullBearNews.overall_sentiment.includes('bear') ? 'rgba(255,59,48,0.12)' : 'rgba(134,134,139,0.12)',
                                        color: bullBearNews.overall_sentiment.includes('bull') ? '#34C759' : bullBearNews.overall_sentiment.includes('bear') ? '#FF3B30' : '#86868B',
                                    }}>
                                        {language === 'zh' ? {
                                            'strongly_bullish': '强烈看多',
                                            'cautiously_bullish': '谨慎看多',
                                            'neutral': '中性',
                                            'cautiously_bearish': '谨慎看空',
                                            'strongly_bearish': '强烈看空',
                                        }[bullBearNews.overall_sentiment] || bullBearNews.overall_sentiment.replace(/_/g, ' ')
                                        : bullBearNews.overall_sentiment.replace(/_/g, ' ')}
                                    </span>
                                )}
                            </div>
                            {/* AI Summary */}
                            {bullBearNews.summary && typeof bullBearNews.summary === 'string' && (
                                <div style={{ fontSize: '0.7rem', color: '#3A3A3C', lineHeight: '1.45', marginBottom: '10px', padding: '6px 8px', backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: '6px', borderLeft: '2px solid rgba(0,0,0,0.08)' }}>
                                    {bullBearNews.summary}
                                </div>
                            )}
                            {/* Bull */}
                            <div style={{ marginBottom: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>
                                    <ArrowTrendingUpIcon style={{ width: 14, height: 14, color: '#34C759' }} />
                                    <span style={{ fontSize: '0.72rem', fontWeight: '600', color: '#34C759' }}>{t('workbench.bullish')} ({bullBearNews.bull.length})</span>
                                </div>
                                {bullBearNews.bull.length === 0 ? (
                                    <div style={{ fontSize: '0.72rem', color: '#86868B', padding: '4px 0' }}>{t('workbench.noBullish')}</div>
                                ) : (
                                    bullBearNews.bull.map((item, idx) => (
                                        <div key={idx} style={{ padding: '4px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <div style={{ fontSize: '0.72rem', fontWeight: '500', color: '#1D1D1F', lineHeight: '1.3', flex: 1 }}>
                                                    {typeof item.title === 'string' ? item.title : ''}
                                                </div>
                                                {item.confidence != null && (
                                                    <span style={{ fontSize: '0.6rem', color: '#34C759', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                                        {Math.round(item.confidence * 100)}%
                                                    </span>
                                                )}
                                            </div>
                                            {typeof item.detail === 'string' && item.detail && (
                                                <div style={{ fontSize: '0.65rem', color: '#86868B', marginTop: '2px', lineHeight: '1.35' }}>
                                                    {item.detail}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                            {/* Bear */}
                            <div style={{ marginBottom: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>
                                    <ArrowTrendingDownIcon style={{ width: 14, height: 14, color: '#FF3B30' }} />
                                    <span style={{ fontSize: '0.72rem', fontWeight: '600', color: '#FF3B30' }}>{t('workbench.bearish')} ({bullBearNews.bear.length})</span>
                                </div>
                                {bullBearNews.bear.length === 0 ? (
                                    <div style={{ fontSize: '0.72rem', color: '#86868B', padding: '4px 0' }}>{t('workbench.noBearish')}</div>
                                ) : (
                                    bullBearNews.bear.map((item, idx) => (
                                        <div key={idx} style={{ padding: '4px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <div style={{ fontSize: '0.72rem', fontWeight: '500', color: '#1D1D1F', lineHeight: '1.3', flex: 1 }}>
                                                    {typeof item.title === 'string' ? item.title : ''}
                                                </div>
                                                {item.confidence != null && (
                                                    <span style={{ fontSize: '0.6rem', color: '#FF3B30', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                                        {Math.round(item.confidence * 100)}%
                                                    </span>
                                                )}
                                            </div>
                                            {typeof item.detail === 'string' && item.detail && (
                                                <div style={{ fontSize: '0.65rem', color: '#86868B', marginTop: '2px', lineHeight: '1.35' }}>
                                                    {item.detail}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                            {/* Catalysts & Risks */}
                            {(bullBearNews.key_catalysts?.length > 0 || bullBearNews.risk_factors?.length > 0) && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '6px' }}>
                                    {bullBearNews.key_catalysts?.length > 0 && (
                                        <div>
                                            <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#34C759', marginBottom: '3px' }}>{t('workbench.catalysts')}</div>
                                            {bullBearNews.key_catalysts.map((c, i) => (
                                                <div key={i} style={{ fontSize: '0.62rem', color: '#3A3A3C', lineHeight: '1.35', padding: '1px 0' }}>{typeof c === 'string' ? c : ''}</div>
                                            ))}
                                        </div>
                                    )}
                                    {bullBearNews.risk_factors?.length > 0 && (
                                        <div>
                                            <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#FF3B30', marginBottom: '3px' }}>{t('workbench.risks')}</div>
                                            {bullBearNews.risk_factors.map((r, i) => (
                                                <div key={i} style={{ fontSize: '0.62rem', color: '#3A3A3C', lineHeight: '1.35', padding: '1px 0' }}>{typeof r === 'string' ? r : ''}</div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Timestamp */}
                            {bullBearNews.generated_at && (
                                <div style={{ fontSize: '0.6rem', color: '#C7C7CC', marginTop: '8px', textAlign: 'right' }}>
                                    {t('workbench.aiAnalysis')} {timeAgo(bullBearNews.generated_at)}
                                </div>
                            )}
                        </div>

                        {/* Right: Company News */}
                        <div style={{ overflowY: 'auto', maxHeight: '280px' }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: '600', color: '#1D1D1F', marginBottom: '8px' }}>
                                <NewspaperIcon style={{ ...sectionIcon, display: 'inline', verticalAlign: 'middle' }} />
                                {t('workbench.relatedNews')}
                            </div>
                            {profileLoading ? (
                                <LoadingSpinner size="small" />
                            ) : companyNews.length === 0 ? (
                                <div style={{ color: '#86868B', fontSize: '0.8rem', padding: '1rem 0' }}>{t('workbench.noNews')}</div>
                            ) : (
                                companyNews.map((n, idx) => (
                                    <div
                                        key={idx}
                                        onClick={() => n.feed_id ? navigate(`/feed/${n.feed_id}`) : n.url && window.open(n.url, '_blank')}
                                        style={{
                                            display: 'block',
                                            padding: '6px 0',
                                            borderBottom: '1px solid rgba(0,0,0,0.04)',
                                            cursor: 'pointer',
                                            color: 'inherit',
                                        }}
                                    >
                                        <div style={{
                                            fontSize: '0.78rem',
                                            fontWeight: '500',
                                            color: '#1D1D1F',
                                            lineHeight: '1.3',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                        }}>
                                            {(language === 'zh' && n.title_zh) ? n.title_zh : n.title}
                                        </div>
                                        <div style={{ fontSize: '0.68rem', color: '#86868B', marginTop: '2px' }}>
                                            {n.source && <span>{n.source}</span>}
                                            {n.source && (n.published_at || n.date) && <span> &middot; </span>}
                                            {(n.published_at || n.date) && <span>{timeAgo(n.published_at || n.date)}</span>}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ====== Portfolio Returns Chart ====== */}
            <div style={{ ...card, marginBottom: '1.5rem', padding: '1rem 1.25rem' }}>
                {/* Header: title + period selector + clear */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                    <span style={{ fontSize: '1.05rem', fontWeight: '700', color: '#1D1D1F' }}>
                        <ArrowTrendingUpIcon style={{ ...sectionIcon, display: 'inline', verticalAlign: 'middle' }} />
                        {t('workbench.portfolio.title')}
                    </span>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        {PORTFOLIO_PERIOD_OPTIONS.map(p => (
                            <button key={p} onClick={() => setPortfolioPeriod(p)} style={{
                                padding: '4px 10px', borderRadius: '6px', border: 'none', fontSize: '0.72rem', fontWeight: portfolioPeriod === p ? '600' : '500',
                                cursor: 'pointer', backgroundColor: portfolioPeriod === p ? '#1D1D1F' : 'rgba(0,0,0,0.04)', color: portfolioPeriod === p ? '#fff' : '#666', transition: 'all 0.15s',
                            }}>{p}</button>
                        ))}
                        <button onClick={() => {
                            setPortfolioPeriod('CUSTOM');
                            if (!portfolioCustomEnd) setPortfolioCustomEnd(new Date().toISOString().slice(0, 10));
                            if (!portfolioCustomStart) {
                                const d = new Date(); d.setMonth(d.getMonth() - 3);
                                setPortfolioCustomStart(d.toISOString().slice(0, 10));
                            }
                        }} style={{
                            padding: '4px 10px', borderRadius: '6px', border: 'none', fontSize: '0.72rem', fontWeight: portfolioPeriod === 'CUSTOM' ? '600' : '500',
                            cursor: 'pointer', backgroundColor: portfolioPeriod === 'CUSTOM' ? '#1D1D1F' : 'rgba(0,0,0,0.04)', color: portfolioPeriod === 'CUSTOM' ? '#fff' : '#666', transition: 'all 0.15s',
                        }}>{t('workbench.portfolio.custom')}</button>
                        {portfolioStocks.length > 0 && (
                            <button onClick={() => setPortfolioStocks([])} style={{
                                padding: '4px 10px', borderRadius: '6px', border: '1px solid rgba(255,59,48,0.2)', fontSize: '0.72rem', fontWeight: '500',
                                cursor: 'pointer', backgroundColor: 'rgba(255,59,48,0.06)', color: '#FF3B30', transition: 'all 0.15s', marginLeft: '4px',
                            }}>{t('workbench.portfolio.clearAll')}</button>
                        )}
                    </div>
                </div>
                {/* Custom date range picker */}
                {portfolioPeriod === 'CUSTOM' && (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', padding: '8px 12px', backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.06)' }}>
                        <span style={{ fontSize: '0.72rem', color: '#86868B', marginRight: '2px' }}>{isZh ? '从' : 'From'}</span>
                        <input type="date" value={portfolioCustomStart} onChange={e => setPortfolioCustomStart(e.target.value)}
                            max={portfolioCustomEnd || undefined}
                            style={{ padding: '5px 10px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)', fontSize: '0.78rem', outline: 'none', color: '#1D1D1F', backgroundColor: '#fff', minWidth: '130px' }} />
                        <span style={{ fontSize: '0.72rem', color: '#86868B' }}>{isZh ? '至' : 'To'}</span>
                        <input type="date" value={portfolioCustomEnd} onChange={e => setPortfolioCustomEnd(e.target.value)}
                            min={portfolioCustomStart || undefined} max={new Date().toISOString().slice(0, 10)}
                            style={{ padding: '5px 10px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)', fontSize: '0.78rem', outline: 'none', color: '#1D1D1F', backgroundColor: '#fff', minWidth: '130px' }} />
                    </div>
                )}

                {/* Saved portfolios row */}
                {(portfolios.length > 0 || portfolioStocks.length > 0) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginBottom: '10px' }}>
                        {portfolios.map(p => {
                            const isActive = p.stocks.length === portfolioStocks.length && p.stocks.every(s => portfolioStocks.includes(s));
                            return (
                                <span key={p.name} style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                                    <button onClick={() => loadPortfolio(p.name)} style={{
                                        padding: '3px 10px', borderRadius: '12px', border: isActive ? '2px solid #007AFF' : '1px solid rgba(0,0,0,0.1)',
                                        fontSize: '0.72rem', fontWeight: isActive ? '700' : '500', cursor: 'pointer',
                                        backgroundColor: isActive ? 'rgba(0,122,255,0.1)' : 'rgba(0,0,0,0.02)', color: isActive ? '#007AFF' : '#1D1D1F', transition: 'all 0.15s',
                                    }}>{p.name}({p.stocks.length})</button>
                                    <button onClick={() => deletePortfolio(p.name)} style={{
                                        width: '16px', height: '16px', borderRadius: '50%', border: 'none', fontSize: '0.6rem', lineHeight: '16px',
                                        cursor: 'pointer', backgroundColor: 'rgba(0,0,0,0.06)', color: '#86868B', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    }}>&times;</button>
                                </span>
                            );
                        })}
                        {portfolioStocks.length > 0 && (
                            <button onClick={() => {
                                const name = window.prompt(t('workbench.portfolio.namePrompt'));
                                if (name && name.trim()) savePortfolio(name.trim(), portfolioStocks);
                            }} style={{
                                padding: '3px 10px', borderRadius: '12px', border: '1px dashed rgba(0,122,255,0.3)',
                                fontSize: '0.72rem', fontWeight: '500', cursor: 'pointer',
                                backgroundColor: 'rgba(0,122,255,0.04)', color: '#007AFF', transition: 'all 0.15s',
                            }}>+ {t('workbench.portfolio.saveCurrent')}</button>
                        )}
                    </div>
                )}

                {/* Stock selector: watchlist chips + manual input */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginBottom: '14px' }}>
                    {watchlist.map((sym, idx) => {
                        const isSelected = portfolioStocks.includes(sym);
                        const colorIdx = isSelected ? portfolioStocks.indexOf(sym) : -1;
                        const atMax = portfolioStocks.length >= PORTFOLIO_MAX_STOCKS;
                        return (
                            <button key={sym} onClick={() => togglePortfolioStock(sym)} style={{
                                padding: '3px 10px', borderRadius: '12px', border: isSelected ? `2px solid ${PORTFOLIO_LINE_COLORS[colorIdx % PORTFOLIO_LINE_COLORS.length]}` : '1px solid rgba(0,0,0,0.1)',
                                fontSize: '0.72rem', fontWeight: isSelected ? '700' : '500', cursor: (!isSelected && atMax) ? 'not-allowed' : 'pointer',
                                backgroundColor: isSelected ? `${PORTFOLIO_LINE_COLORS[colorIdx % PORTFOLIO_LINE_COLORS.length]}15` : 'rgba(0,0,0,0.02)',
                                color: isSelected ? PORTFOLIO_LINE_COLORS[colorIdx % PORTFOLIO_LINE_COLORS.length] : (atMax ? '#C7C7CC' : '#666'),
                                transition: 'all 0.15s',
                            }}>{sym}</button>
                        );
                    })}
                    <div style={{ display: 'flex', gap: '4px', marginLeft: '4px' }}>
                        <input
                            value={portfolioInput}
                            onChange={e => setPortfolioInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && addPortfolioInput()}
                            placeholder={t('workbench.portfolio.addSymbol')}
                            style={{ width: '90px', padding: '3px 8px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.08)', fontSize: '0.72rem', outline: 'none' }}
                        />
                        <button onClick={addPortfolioInput} style={{
                            padding: '3px 8px', borderRadius: '8px', border: 'none', backgroundColor: '#007AFF', color: '#fff', fontSize: '0.7rem', fontWeight: '600', cursor: 'pointer',
                        }}><PlusIcon style={{ width: '10px', display: 'inline', verticalAlign: 'middle' }} /></button>
                    </div>
                </div>

                {/* Chart area */}
                {portfolioStocks.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '1.5rem 0', color: '#86868B', fontSize: '0.82rem' }}>
                        {t('workbench.portfolio.noData')}
                    </div>
                ) : portfolioLoading && !portfolioChartData ? (
                    <LoadingSpinner size="small" />
                ) : portfolioChartData ? (() => {
                    const { syms, commonDates, series, portfolioReturns, minY, maxY } = portfolioChartData;
                    const W = 700, H = 210, padL = 52, padR = 90, padT = 16, padB = 26;
                    const chartW = W - padL - padR;
                    const chartH = H - padT - padB;
                    const rangeY = maxY - minY || 1;

                    const toX = (i) => padL + (i / (commonDates.length - 1)) * chartW;
                    const toY = (v) => padT + ((maxY - v) / rangeY) * chartH;

                    // Y-axis ticks
                    const yTicks = [];
                    const yStep = rangeY / 4;
                    for (let i = 0; i <= 4; i++) {
                        const val = minY + yStep * i;
                        yTicks.push(val);
                    }
                    // Always include 0 line if in range
                    const zeroInRange = minY <= 0 && maxY >= 0;

                    // X-axis labels (4-5 evenly spaced)
                    const xLabelCount = Math.min(5, commonDates.length);
                    const xLabels = [];
                    for (let i = 0; i < xLabelCount; i++) {
                        const idx = Math.round((i / (xLabelCount - 1)) * (commonDates.length - 1));
                        const d = commonDates[idx];
                        xLabels.push({ idx, label: d.slice(5) }); // MM-DD
                    }

                    const makeLine = (vals) => vals.map((v, i) => `${toX(i)},${toY(v)}`).join(' ');

                    const lastIdx = commonDates.length - 1;
                    const hoverIdx = portfolioHover != null ? Math.min(portfolioHover, lastIdx) : null;

                    return (
                        <div style={{ position: 'relative' }}>
                            <svg
                                viewBox={`0 0 ${W} ${H}`}
                                style={{ width: '100%', height: 'auto', display: 'block' }}
                                onMouseMove={e => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const x = ((e.clientX - rect.left) / rect.width) * W;
                                    const idx = Math.round(((x - padL) / chartW) * (commonDates.length - 1));
                                    if (idx >= 0 && idx <= lastIdx) setPortfolioHover(idx);
                                }}
                                onMouseLeave={() => setPortfolioHover(null)}
                            >
                                {/* Y grid lines */}
                                {yTicks.map((val, i) => (
                                    <g key={i}>
                                        <line x1={padL} y1={toY(val)} x2={W - padR} y2={toY(val)} stroke="rgba(0,0,0,0.06)" strokeWidth="0.5" />
                                        <text x={padL - 6} y={toY(val) + 3} textAnchor="end" fontSize="9" fill="#86868B">
                                            {val >= 0 ? '+' : ''}{val.toFixed(0)}%
                                        </text>
                                    </g>
                                ))}
                                {/* 0% baseline */}
                                {zeroInRange && (
                                    <line x1={padL} y1={toY(0)} x2={W - padR} y2={toY(0)} stroke="rgba(0,0,0,0.15)" strokeWidth="0.5" strokeDasharray="4,3" />
                                )}

                                {/* X-axis labels */}
                                {xLabels.map(({ idx, label }, i) => (
                                    <text key={i} x={toX(idx)} y={H - 8} textAnchor="middle" fontSize="9" fill="#86868B">{label}</text>
                                ))}

                                {/* Stock lines */}
                                {syms.map((sym, si) => {
                                    const color = PORTFOLIO_LINE_COLORS[portfolioStocks.indexOf(sym) % PORTFOLIO_LINE_COLORS.length];
                                    const finalVal = series[sym][lastIdx];
                                    return (
                                        <g key={sym}>
                                            <polyline points={makeLine(series[sym])} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
                                            <text x={W - padR + 6} y={toY(finalVal) + 3} fontSize="9" fontWeight="600" fill={color}>
                                                {sym} {finalVal >= 0 ? '+' : ''}{finalVal.toFixed(1)}%
                                            </text>
                                        </g>
                                    );
                                })}

                                {/* Portfolio line (dashed, bold) */}
                                {syms.length > 1 && (() => {
                                    const finalVal = portfolioReturns[lastIdx];
                                    return (
                                        <g>
                                            <polyline points={makeLine(portfolioReturns)} fill="none" stroke="#1D1D1F" strokeWidth="2.5" strokeDasharray="6,3" strokeLinejoin="round" />
                                            <text x={W - padR + 6} y={toY(finalVal) + 3} fontSize="9" fontWeight="700" fill="#1D1D1F">
                                                {t('workbench.portfolio.portfolioLabel')} {finalVal >= 0 ? '+' : ''}{finalVal.toFixed(1)}%
                                            </text>
                                        </g>
                                    );
                                })()}

                                {/* Hover crosshair + tooltip */}
                                {hoverIdx != null && (
                                    <g>
                                        <line x1={toX(hoverIdx)} y1={padT} x2={toX(hoverIdx)} y2={H - padB} stroke="rgba(0,0,0,0.2)" strokeWidth="0.5" strokeDasharray="3,2" />
                                        {syms.map((sym, si) => {
                                            const color = PORTFOLIO_LINE_COLORS[portfolioStocks.indexOf(sym) % PORTFOLIO_LINE_COLORS.length];
                                            const val = series[sym][hoverIdx];
                                            return <circle key={sym} cx={toX(hoverIdx)} cy={toY(val)} r="3" fill={color} stroke="#fff" strokeWidth="1" />;
                                        })}
                                        {syms.length > 1 && (
                                            <circle cx={toX(hoverIdx)} cy={toY(portfolioReturns[hoverIdx])} r="3" fill="#1D1D1F" stroke="#fff" strokeWidth="1" />
                                        )}
                                        {/* Tooltip box */}
                                        <foreignObject x={Math.min(toX(hoverIdx) + 8, W - 160)} y={padT} width="150" height={20 + (syms.length + (syms.length > 1 ? 1 : 0)) * 14}>
                                            <div xmlns="http://www.w3.org/1999/xhtml" style={{
                                                background: 'rgba(255,255,255,0.95)', borderRadius: '6px', padding: '4px 8px', fontSize: '9px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', border: '1px solid rgba(0,0,0,0.06)',
                                            }}>
                                                <div style={{ fontWeight: '600', color: '#1D1D1F', marginBottom: '2px' }}>{commonDates[hoverIdx]}</div>
                                                {syms.map((sym) => {
                                                    const color = PORTFOLIO_LINE_COLORS[portfolioStocks.indexOf(sym) % PORTFOLIO_LINE_COLORS.length];
                                                    const val = series[sym][hoverIdx];
                                                    return (
                                                        <div key={sym} style={{ color, fontWeight: '500' }}>
                                                            {sym}: {val >= 0 ? '+' : ''}{val.toFixed(2)}%
                                                        </div>
                                                    );
                                                })}
                                                {syms.length > 1 && (
                                                    <div style={{ color: '#1D1D1F', fontWeight: '600', borderTop: '1px solid rgba(0,0,0,0.08)', marginTop: '2px', paddingTop: '2px' }}>
                                                        {t('workbench.portfolio.portfolioLabel')}: {portfolioReturns[hoverIdx] >= 0 ? '+' : ''}{portfolioReturns[hoverIdx].toFixed(2)}%
                                                    </div>
                                                )}
                                            </div>
                                        </foreignObject>
                                    </g>
                                )}
                            </svg>
                        </div>
                    );
                })() : (
                    <div style={{ textAlign: 'center', padding: '1.5rem 0', color: '#86868B', fontSize: '0.82rem' }}>
                        {t('workbench.portfolio.noData')}
                    </div>
                )}

                {/* Legend */}
                {portfolioChartData && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                        {portfolioChartData.syms.map(sym => {
                            const color = PORTFOLIO_LINE_COLORS[portfolioStocks.indexOf(sym) % PORTFOLIO_LINE_COLORS.length];
                            const finalVal = portfolioChartData.series[sym][portfolioChartData.commonDates.length - 1];
                            return (
                                <div key={sym} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem' }}>
                                    <span style={{ width: '14px', height: '2px', backgroundColor: color, display: 'inline-block', borderRadius: '1px' }} />
                                    <span style={{ fontWeight: '600', color }}>{sym}</span>
                                    <span style={{ color: finalVal >= 0 ? '#34C759' : '#FF3B30', fontWeight: '500' }}>
                                        {finalVal >= 0 ? '+' : ''}{finalVal.toFixed(1)}%
                                    </span>
                                </div>
                            );
                        })}
                        {portfolioChartData.syms.length > 1 && (() => {
                            const finalVal = portfolioChartData.portfolioReturns[portfolioChartData.commonDates.length - 1];
                            return (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem' }}>
                                    <span style={{ width: '14px', height: '2px', backgroundColor: '#1D1D1F', display: 'inline-block', borderRadius: '1px', borderTop: '1px dashed #1D1D1F' }} />
                                    <span style={{ fontWeight: '700', color: '#1D1D1F' }}>{t('workbench.portfolio.portfolioLabel')}</span>
                                    <span style={{ color: finalVal >= 0 ? '#34C759' : '#FF3B30', fontWeight: '600' }}>
                                        {finalVal >= 0 ? '+' : ''}{finalVal.toFixed(1)}%
                                    </span>
                                </div>
                            );
                        })()}
                    </div>
                )}
            </div>

            {/* ====== Row 2: Investment Discovery ====== */}
            <div style={{ ...card, padding: '0', overflow: 'hidden' }}>
                {/* Header */}
                <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '1.05rem', fontWeight: '700', color: '#1D1D1F' }}>{t('workbench.investmentDiscovery')}</span>
                </div>

                {/* 3-column grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>

                    {/* ---- Col 1: Smart Money Moves ---- */}
                    <div style={{ borderRight: '1px solid rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', maxHeight: '520px' }}>
                        <div style={{ padding: '0.7rem 1rem', borderBottom: '1px solid rgba(0,0,0,0.06)', backgroundColor: 'rgba(0,0,0,0.015)' }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: '600', color: '#1D1D1F', marginBottom: '6px' }}>{t('workbench.smartMoneyMoves')}</div>
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                {ARK_FUNDS.map(fund => (
                                    <button key={fund} onClick={() => setSelectedFund(fund)} style={{
                                        padding: '3px 9px', borderRadius: '6px', border: 'none', fontSize: '0.72rem', fontWeight: selectedFund === fund ? '600' : '500',
                                        cursor: 'pointer', backgroundColor: selectedFund === fund ? '#1D1D1F' : 'rgba(0,0,0,0.04)', color: selectedFund === fund ? '#fff' : '#666', transition: 'all 0.15s',
                                    }}>{fund}</button>
                                ))}
                            </div>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            {arkLoading && arkChanges.length === 0 ? (
                                <LoadingSpinner size="small" />
                            ) : arkChanges.length === 0 ? (
                                <div style={{ textAlign: 'center', color: '#86868B', padding: '1.5rem 0', fontSize: '0.8rem' }}>{t('workbench.noChanges')}</div>
                            ) : (
                                arkChanges.map((c, idx) => {
                                    const isNew = c.change_type === 'new';
                                    const isSold = c.change_type === 'sold_out';
                                    const isIncrease = c.change_type === 'increase';
                                    return (
                                        <div key={idx} style={{ padding: '6px 1rem', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#1D1D1F' }}>{c.ticker}</span>
                                                    {(isNew || isSold) ? (
                                                        <span style={{ ...tag, padding: '1px 6px', fontSize: '0.62rem',
                                                            backgroundColor: isNew ? 'rgba(52,199,89,0.12)' : 'rgba(255,59,48,0.12)',
                                                            color: isNew ? '#34C759' : '#FF3B30',
                                                        }}>{isNew ? 'NEW' : 'SOLD'}</span>
                                                    ) : (
                                                        <span style={{ fontSize: '0.72rem', fontWeight: '500', color: isIncrease ? '#34C759' : '#FF3B30' }}>
                                                            {isIncrease ? (isZh ? '↑ 增持' : '↑ Increase') : (isZh ? '↓ 减持' : '↓ Decrease')}
                                                        </span>
                                                    )}
                                                </div>
                                                {c.shares_change_pct != null && (
                                                    <span style={{ fontSize: '0.75rem', fontWeight: '600', color: c.shares_change_pct > 0 ? '#34C759' : '#FF3B30' }}>
                                                        {c.shares_change_pct > 0 ? '+' : ''}{c.shares_change_pct.toFixed(1)}%
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1px' }}>
                                                <span style={{ fontSize: '0.68rem', color: '#86868B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>{c.company}</span>
                                                {c.weight_change != null && (
                                                    <span style={{ fontSize: '0.62rem', color: '#AEAEB2' }}>wt {c.weight_change > 0 ? '+' : ''}{c.weight_change.toFixed(2)}%</span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                        {/* Stats bar */}
                        <div style={{ padding: '6px 1rem', borderTop: '1px solid rgba(0,0,0,0.06)', backgroundColor: 'rgba(0,0,0,0.015)', display: 'flex', gap: '10px', fontSize: '0.68rem' }}>
                            <span style={{ color: '#34C759', fontWeight: '600' }}>{arkChangeStats.new} new</span>
                            <span style={{ color: '#34C759', fontWeight: '600' }}>{arkChangeStats.increase} increase</span>
                            <span style={{ color: '#FF3B30', fontWeight: '600' }}>{arkChangeStats.decrease} decrease</span>
                            <span style={{ color: '#FF3B30', fontWeight: '600' }}>{arkChangeStats.sold_out} sold</span>
                        </div>
                    </div>

                    {/* ---- Col 2: Guru Portfolio ---- */}
                    <div style={{ borderRight: '1px solid rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', maxHeight: '520px' }}>
                        <div style={{ padding: '0.7rem 1rem', borderBottom: '1px solid rgba(0,0,0,0.06)', backgroundColor: 'rgba(0,0,0,0.015)' }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: '600', color: '#1D1D1F', marginBottom: '6px' }}>{t('workbench.guruPortfolio')}</div>
                            {investors.length > 0 && (
                                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                    {investors.map(inv => {
                                        const key = inv.key || inv.name;
                                        const isActive = selectedInvestor === key;
                                        return (
                                            <button key={key} onClick={() => setSelectedInvestor(key)} style={{
                                                padding: '3px 9px', borderRadius: '6px', border: 'none', fontSize: '0.72rem', fontWeight: isActive ? '600' : '500',
                                                cursor: 'pointer', backgroundColor: isActive ? '#1D1D1F' : 'rgba(0,0,0,0.04)', color: isActive ? '#fff' : '#666', transition: 'all 0.15s',
                                            }}>{typeof inv.display_name === 'string' ? inv.display_name : (typeof inv.name === 'string' ? inv.name : key)}</button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        {/* Investor summary */}
                        {investorInfo && (
                            <div style={{ padding: '5px 1rem', borderBottom: '1px solid rgba(0,0,0,0.04)', fontSize: '0.68rem', color: '#86868B', display: 'flex', gap: '10px', backgroundColor: 'rgba(0,122,255,0.02)' }}>
                                {investorInfo.last_filing_date && <span>Filed: {new Date(investorInfo.last_filing_date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                                {investorInfo.assets_under_management && <span>AUM: {investorInfo.assets_under_management >= 1e9 ? `$${(investorInfo.assets_under_management / 1e9).toFixed(1)}B` : `$${(investorInfo.assets_under_management / 1e6).toFixed(0)}M`}</span>}
                                {investorInfo.latest_summary?.num_holdings && <span>{investorInfo.latest_summary.num_holdings} holdings</span>}
                            </div>
                        )}
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            {holdings.length === 0 ? (
                                <div style={{ textAlign: 'center', color: '#86868B', padding: '1.5rem 0', fontSize: '0.8rem' }}>{t('workbench.noHoldings')}</div>
                            ) : (<>
                                {/* Table header */}
                                <div style={{ display: 'flex', padding: '5px 1rem', borderBottom: '1px solid rgba(0,0,0,0.08)', backgroundColor: 'rgba(0,0,0,0.02)', position: 'sticky', top: 0, zIndex: 1 }}>
                                    <span style={{ flex: 1, fontSize: '0.6rem', fontWeight: '600', color: '#AEAEB2', textTransform: 'uppercase', letterSpacing: '0.3px' }}>股票代码 / 公司</span>
                                    <span style={{ width: '60px', fontSize: '0.6rem', fontWeight: '600', color: '#AEAEB2', textTransform: 'uppercase', letterSpacing: '0.3px', textAlign: 'right' }}>市值</span>
                                    <span style={{ width: '42px', fontSize: '0.6rem', fontWeight: '600', color: '#AEAEB2', textTransform: 'uppercase', letterSpacing: '0.3px', textAlign: 'right' }}>权重</span>
                                    <span style={{ width: '52px', fontSize: '0.6rem', fontWeight: '600', color: '#AEAEB2', textTransform: 'uppercase', letterSpacing: '0.3px', textAlign: 'right' }}>变化</span>
                                </div>
                                {holdings.map((h, idx) => {
                                    const pct = h.percent_of_portfolio ?? h.portfolio_percent;
                                    const name = h.company_name || h.company || h.name || '';
                                    const mv = h.market_value;
                                    const mvStr = mv ? (mv >= 1e9 ? `$${(mv / 1e9).toFixed(1)}B` : mv >= 1e6 ? `$${(mv / 1e6).toFixed(0)}M` : `$${(mv / 1e3).toFixed(0)}K`) : '';
                                    const hasChange = h.shares_change != null && h.shares_change !== 0;
                                    const absPct = h.shares_change_percent ? Math.abs(h.shares_change_percent) : 0;
                                    const pctStr = absPct >= 100 ? `${(absPct / 100 + 1).toFixed(1)}x` : `${absPct.toFixed(1)}%`;
                                    return (
                                        <div key={idx} style={{ display: 'flex', alignItems: 'center', padding: '6px 1rem', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px' }}>
                                                    <span style={{ fontSize: '0.78rem', fontWeight: '700', color: '#007AFF', flexShrink: 0 }}>{h.ticker || h.symbol || '--'}</span>
                                                    <span style={{ fontSize: '0.62rem', color: '#AEAEB2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                                                </div>
                                                <div style={{ fontSize: '0.62rem', color: '#C7C7CC', marginTop: '1px' }}>{h.shares ? `${Number(h.shares).toLocaleString()}` : ''}</div>
                                            </div>
                                            <span style={{ width: '60px', fontSize: '0.72rem', fontWeight: '600', color: '#1D1D1F', textAlign: 'right', flexShrink: 0 }}>{mvStr}</span>
                                            <span style={{ width: '42px', fontSize: '0.62rem', fontWeight: '600', color: '#5856D6', textAlign: 'right', flexShrink: 0 }}>{pct != null ? `${Number(pct).toFixed(1)}%` : ''}</span>
                                            <span style={{ width: '52px', fontSize: '0.62rem', fontWeight: '500', textAlign: 'right', flexShrink: 0, color: hasChange ? (h.shares_change > 0 ? '#34C759' : '#FF3B30') : '#C7C7CC' }}>
                                                {hasChange ? `${h.shares_change > 0 ? '↑' : '↓'} ${h.shares_change_percent ? pctStr : ''}` : '— —'}
                                            </span>
                                        </div>
                                    );
                                })}
                            </>)}
                        </div>
                    </div>

                    {/* ---- Col 3: Investment Signals ---- */}
                    <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '520px' }}>
                        <div style={{ padding: '0.7rem 1rem', borderBottom: '1px solid rgba(0,0,0,0.06)', backgroundColor: 'rgba(0,0,0,0.015)' }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: '600', color: '#1D1D1F' }}>{t('workbench.investmentSignals')}</div>
                            <div style={{ fontSize: '0.65rem', color: '#AEAEB2', marginTop: '2px' }}>{t('workbench.signalsDesc')}</div>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            {/* Consensus */}
                            <div style={{ padding: '0.7rem 1rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '6px' }}>
                                    <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#FF9500', display: 'inline-block' }}></span>
                                    <span style={{ fontSize: '0.7rem', fontWeight: '600', color: '#1D1D1F', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t('workbench.consensusHoldings')}</span>
                                    <span style={{ fontSize: '0.62rem', color: '#AEAEB2' }}>({investmentSignals.consensus.length})</span>
                                </div>
                                {investmentSignals.consensus.length === 0 ? (
                                    <div style={{ fontSize: '0.72rem', color: '#AEAEB2' }}>{t('workbench.noOverlap')}</div>
                                ) : investmentSignals.consensus.map((s, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', marginBottom: '3px', borderRadius: '6px', backgroundColor: 'rgba(255,149,0,0.06)' }}>
                                        <div>
                                            <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#1D1D1F' }}>{s.ticker}</span>
                                            <span style={{ fontSize: '0.68rem', color: '#86868B', marginLeft: '5px' }}>{s.company}</span>
                                        </div>
                                        <div style={{ textAlign: 'right', fontSize: '0.62rem', color: '#86868B' }}>
                                            {s.arkWeight != null && <div>ARK {typeof s.arkWeight === 'number' ? s.arkWeight.toFixed(1) : s.arkWeight}%</div>}
                                            {s.guruWeight != null && <div>Guru {s.guruWeight}%</div>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {/* Big Buys */}
                            <div style={{ padding: '0.7rem 1rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '6px' }}>
                                    <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#34C759', display: 'inline-block' }}></span>
                                    <span style={{ fontSize: '0.7rem', fontWeight: '600', color: '#1D1D1F', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t('workbench.bigBuys')}</span>
                                    <span style={{ fontSize: '0.62rem', color: '#AEAEB2' }}>({investmentSignals.bigBuys.length})</span>
                                </div>
                                {investmentSignals.bigBuys.length === 0 ? (
                                    <div style={{ fontSize: '0.72rem', color: '#AEAEB2' }}>{t('workbench.noBigBuys')}</div>
                                ) : investmentSignals.bigBuys.map((b, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', marginBottom: '3px', borderRadius: '6px', backgroundColor: 'rgba(52,199,89,0.06)' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#1D1D1F' }}>{b.ticker}</span>
                                            {b.change_type === 'new' && (
                                                <span style={{ ...tag, padding: '1px 5px', fontSize: '0.58rem', backgroundColor: 'rgba(52,199,89,0.12)', color: '#34C759' }}>NEW</span>
                                            )}
                                        </div>
                                        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#34C759' }}>
                                            {b.shares_change_pct != null ? `+${Math.abs(b.shares_change_pct).toFixed(1)}%` : 'New'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            {/* Exit Alerts */}
                            <div style={{ padding: '0.7rem 1rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '6px' }}>
                                    <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#FF3B30', display: 'inline-block' }}></span>
                                    <span style={{ fontSize: '0.7rem', fontWeight: '600', color: '#1D1D1F', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t('workbench.exitAlerts')}</span>
                                    <span style={{ fontSize: '0.62rem', color: '#AEAEB2' }}>({investmentSignals.exits.length})</span>
                                </div>
                                {investmentSignals.exits.length === 0 ? (
                                    <div style={{ fontSize: '0.72rem', color: '#AEAEB2' }}>{t('workbench.noExitSignals')}</div>
                                ) : investmentSignals.exits.map((e, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', marginBottom: '3px', borderRadius: '6px', backgroundColor: 'rgba(255,59,48,0.06)' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#1D1D1F' }}>{e.ticker}</span>
                                            {e.type === 'sold_out' ? (
                                                <span style={{ ...tag, padding: '1px 5px', fontSize: '0.58rem', backgroundColor: 'rgba(255,59,48,0.12)', color: '#FF3B30' }}>SOLD</span>
                                            ) : (
                                                <span style={{ fontSize: '0.68rem', fontWeight: '500', color: '#FF3B30' }}>↓ {e.changePct ? `${Math.abs(e.changePct).toFixed(1)}%` : 'Reduced'}</span>
                                            )}
                                        </div>
                                        <span style={{ fontSize: '0.62rem', color: '#AEAEB2' }}>{e.source}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CSS animation */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default WorkbenchPage;
